import { WorkspacePlan } from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  Card,
  CardContent,
  cn,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { Sparkles } from 'lucide-react';
import { useCallback } from 'react';

import {
  FEATURE_METADATA,
  FEATURE_MINIMUM_PLAN,
  type FeatureKey,
  PLAN_LABEL_KEYS,
} from '@/core/authz/entitlement-catalog';
import type { UpgradeRequiredContext } from '@/core/authz/parse-upgrade-required-error';
import { ShellLink } from '@/core/components/navigation/shell-link';
import { billingUpgradePath } from '@/core/billing/upgrade-focus';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { resolveDictKey } from '@/core/lib/resolve-dict-key';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { canPurchaseBillingInApp } from '@/core/billing/can-purchase-billing-in-app';

export type FeatureUpgradeTeaser = {
  title: string;
  benefits?: string[];
};

type UpgradeDict = {
  genericTitle: string;
  genericDescription: string;
  featureLockedTitle: string;
  featureLockedDescription: string;
  featureLockedCompactDescription: string;
  quotaExceededTitle: string;
  quotaExceededDescription: string;
  feedbackPaygTitle?: string;
  feedbackPaygDescription?: string;
  feedbackPaygCta?: string;
  unknownPlan: string;
  cta: string;
  featureTeasers?: Partial<Record<FeatureKey, FeatureUpgradeTeaser>>;
};

export type UpgradeNotice = {
  title: string;
  description: string;
  compactDescription: string;
  benefits?: string[];
  cta: string;
};

export function resolveUpgradeNotice({
  upgradeDict,
  feature,
  context,
  plan,
  getFeatureLabel,
  getPlanLabel,
}: {
  upgradeDict: UpgradeDict;
  feature?: FeatureKey;
  context?: UpgradeRequiredContext | null;
  plan?: WorkspacePlan | null;
  getFeatureLabel: (feature: FeatureKey) => string;
  getPlanLabel: (plan: WorkspacePlan) => string;
}): UpgradeNotice {
  const requiredPlan = feature
    ? FEATURE_MINIMUM_PLAN[feature]
    : WorkspacePlan.TEAMS;
  const currentPlanLabel = plan ? getPlanLabel(plan) : upgradeDict.unknownPlan;
  const planLine = upgradeDict.featureLockedCompactDescription.replace(
    '{plan}',
    getPlanLabel(requiredPlan),
  );

  if (feature) {
    const teaser = upgradeDict.featureTeasers?.[feature];
    const benefits = teaser?.benefits?.filter((b) => b.trim().length > 0);

    return {
      title:
        teaser?.title ??
        upgradeDict.featureLockedTitle.replace(
          '{feature}',
          getFeatureLabel(feature),
        ),
      description: teaser
        ? planLine
        : upgradeDict.featureLockedDescription
            .replace('{plan}', getPlanLabel(requiredPlan))
            .replace('{currentPlan}', currentPlanLabel),
      compactDescription: planLine,
      benefits: benefits && benefits.length > 0 ? benefits : undefined,
      cta: upgradeDict.cta,
    };
  }

  if (context?.type === 'feedback_payg') {
    const paygDescription = (
      upgradeDict.feedbackPaygDescription ??
      upgradeDict.quotaExceededDescription
    )
      .replace('{current}', String(context.current))
      .replace('{limit}', String(context.limit))
      .replace('{additional}', String(context.additional));
    return {
      title: upgradeDict.feedbackPaygTitle ?? upgradeDict.quotaExceededTitle,
      description: paygDescription,
      compactDescription: paygDescription,
      cta: upgradeDict.feedbackPaygCta ?? upgradeDict.cta,
    };
  }

  if (context?.type === 'quota') {
    const quotaDescription = upgradeDict.quotaExceededDescription
      .replace('{current}', String(context.current))
      .replace('{limit}', String(context.limit));
    return {
      title: upgradeDict.quotaExceededTitle,
      description: quotaDescription,
      compactDescription: quotaDescription,
      cta: upgradeDict.cta,
    };
  }

  return {
    title: upgradeDict.genericTitle,
    description: upgradeDict.genericDescription,
    compactDescription: upgradeDict.genericDescription,
    cta: upgradeDict.cta,
  };
}

export function useEntitlementLabels() {
  const dict = useDictionary();

  const getFeatureLabel = useCallback(
    (feature: FeatureKey): string => {
      const key = FEATURE_METADATA[feature].labelKey;
      return resolveDictKey(dict as Record<string, unknown>, key) ?? feature;
    },
    [dict],
  );

  const getPlanLabel = useCallback(
    (plan: WorkspacePlan): string => {
      const key = PLAN_LABEL_KEYS[plan];
      return resolveDictKey(dict as Record<string, unknown>, key) ?? plan;
    },
    [dict],
  );

  return { getFeatureLabel, getPlanLabel };
}

interface PlanBadgeProps {
  className?: string;
}

interface PlanLockBadgeProps {
  feature: FeatureKey;
  className?: string;
}

/** Compact plan pill for locked nav items (DEV-358). Hidden when entitled. */
export function PlanLockBadge({ feature, className }: PlanLockBadgeProps) {
  const { isFeatureEnabled, isLoading } = useEntitlements();
  const { getPlanLabel } = useEntitlementLabels();

  if (isLoading || isFeatureEnabled(feature)) return null;

  return (
    <Badge
      variant="secondary"
      className={cn('h-5 shrink-0 px-1.5 text-[11px] font-semibold', className)}
    >
      {getPlanLabel(FEATURE_MINIMUM_PLAN[feature])}
    </Badge>
  );
}

export function PlanBadge({ className }: PlanBadgeProps) {
  const { plan, isLoading } = useEntitlements();
  const { getPlanLabel } = useEntitlementLabels();

  if (isLoading) {
    return <Skeleton className="h-5 w-16 rounded-full" />;
  }

  if (!plan) return null;

  return (
    <Badge variant="secondary" className={className}>
      {getPlanLabel(plan)}
    </Badge>
  );
}

interface UpgradeCardProps {
  feature?: FeatureKey;
  context?: UpgradeRequiredContext;
  title?: string;
  /**
   * Optional override. Defaults to the short compact notice (Phase 7 density).
   * Pass an explicit longer string only when the surface needs quota/payg detail.
   */
  description?: string;
  className?: string;
  /**
   * Dense inline chrome (default). Set `false` for a slightly larger card still
   * using short body copy — not the old essay stack.
   */
  compact?: boolean;
}

function UpgradeBenefits({ benefits }: { benefits: string[] }) {
  return (
    <ul className="mt-1.5 list-none space-y-1">
      {benefits.map((benefit) => (
        <li
          key={benefit}
          className="flex gap-2 text-xs leading-snug text-muted-foreground"
        >
          <span
            className="mt-1.5 size-1 shrink-0 rounded-full bg-primary/60"
            aria-hidden
          />
          <span>{benefit}</span>
        </li>
      ))}
    </ul>
  );
}

export function UpgradeCard({
  feature,
  context,
  title,
  description,
  className,
  compact = true,
}: UpgradeCardProps) {
  const dict = useDictionary();
  const upgradeDict = dict.entitlements.upgrade;
  const { plan, isLoading } = useEntitlements();
  const { getFeatureLabel, getPlanLabel } = useEntitlementLabels();

  const resolvedFeature =
    feature ?? (context?.type === 'feature' ? context.feature : undefined);
  const notice = resolveUpgradeNotice({
    upgradeDict,
    feature: resolvedFeature,
    context,
    plan,
    getFeatureLabel,
    getPlanLabel,
  });

  const resolvedTitle = title === '' ? undefined : (title ?? notice.title);
  const benefits = description === undefined ? notice.benefits : undefined;
  /** Prefer short plan line; callers may pass longer quota/payg copy. */
  const bodyDescription = description ?? notice.compactDescription;
  const ctaLabel = notice.cta;
  const showBenefits = benefits != null && benefits.length > 0;
  const showPurchaseCta = canPurchaseBillingInApp();

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className={compact ? 'p-3' : 'p-5'}>
          <Skeleton className="mb-2 h-5 w-2/3" />
          <Skeleton className="h-4 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <div
        className={cn(
          'flex flex-col gap-2.5 rounded-lg border border-primary/20 bg-primary/5 p-3',
          className,
        )}
      >
        <div className="flex min-w-0 items-start gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            {resolvedTitle ? (
              <p className="text-sm font-medium leading-snug">
                {resolvedTitle}
              </p>
            ) : null}
            {showBenefits ? (
              <UpgradeBenefits benefits={benefits} />
            ) : (
              <p className="text-xs leading-snug text-muted-foreground">
                {bodyDescription}
              </p>
            )}
            {showBenefits ? (
              <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground/90">
                {bodyDescription}
              </p>
            ) : null}
          </div>
        </div>
        {showPurchaseCta ? (
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-full shrink-0 px-3 text-xs sm:w-auto sm:self-start sm:text-sm"
            asChild
          >
            <ShellLink to={billingUpgradePath(resolvedFeature)}>
              {ctaLabel}
            </ShellLink>
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <Card className={className}>
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            {resolvedTitle ? (
              <p className="text-[15px] font-semibold leading-snug">
                {resolvedTitle}
              </p>
            ) : null}
            {showBenefits ? (
              <UpgradeBenefits benefits={benefits} />
            ) : (
              <p className="mt-0.5 text-[13px] leading-snug text-muted-foreground">
                {bodyDescription}
              </p>
            )}
            {showBenefits ? (
              <p className="mt-2 text-xs leading-snug text-muted-foreground">
                {bodyDescription}
              </p>
            ) : null}
          </div>
        </div>
        {showPurchaseCta ? (
          <Button
            variant="primary"
            size="sm"
            className="w-full sm:w-auto sm:self-start"
            asChild
          >
            <ShellLink to={billingUpgradePath(resolvedFeature)}>
              {ctaLabel}
            </ShellLink>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
